/**
 * Invoice detail page (#45 + #46 + #47 + #48 + #49 + #50 + #51 + #52).
 *
 * Composes the split-view layout, sticky header, error banner, PDF preview
 * (#46), edit form (#47), reprocess button (#48) plus the Sprint-2
 * extensions: manual-review actions (#49), comments panel (#50),
 * audit-trail (#51) and the export menu (#52). Owns the detail
 * TanStack Query and the update mutation; all leaf components receive
 * what they need via props.
 */
import type { InvoiceBlob } from '@invoice/shared/schemas';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { AuthRequiredState } from '@/components/AuthRequiredState';
import { DeleteInvoiceButton } from '@/components/DeleteInvoiceButton';
import { InvoiceAuditTrail } from '@/components/InvoiceAuditTrail';
import { InvoiceCommentsPanel } from '@/components/InvoiceCommentsPanel';
import { InvoiceDetailHeader } from '@/components/InvoiceDetailHeader';
import { InvoiceDetailLayout } from '@/components/InvoiceDetailLayout';
import { InvoiceEditForm, makeEmptyBlob } from '@/components/InvoiceEditForm';
import { InvoiceErrorBanner } from '@/components/InvoiceErrorBanner';
import { InvoiceExceptionFocusPanel } from '@/components/InvoiceExceptionFocusPanel';
import { InvoiceExportMenu } from '@/components/InvoiceExportMenu';
import { InvoiceManualReviewActions } from '@/components/InvoiceManualReviewActions';
import { InvoiceSapSyncPanel } from '@/components/InvoiceSapSyncPanel';
import { type PdfHighlightTarget, PdfPreview } from '@/components/PdfPreview';
import { ReprocessButton } from '@/components/ReprocessButton';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useI18n } from '@/lib/i18n';
import {
  ApiError,
  invoiceFileUrl,
  useInvoiceDetail,
  useUpdateInvoice,
} from '@/lib/queries/invoiceDetail';

export const Route = createFileRoute('/invoices/$id')({
  component: InvoiceDetailPage,
});

function InvoiceDetailPage() {
  const { language } = useI18n();
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const detailQuery = useInvoiceDetail(id);
  const updateMutation = useUpdateInvoice(id);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [pdfHighlight, setPdfHighlight] = useState<{
    invoiceId: string;
    target: PdfHighlightTarget;
  } | null>(null);

  if (detailQuery.isPending) {
    return <DetailSkeleton />;
  }

  if (detailQuery.isError) {
    const isNotFound = detailQuery.error instanceof ApiError && detailQuery.error.status === 404;
    const isUnauthorized =
      detailQuery.error instanceof ApiError && detailQuery.error.status === 401;
    if (isUnauthorized) {
      return <AuthRequiredState message={detailQuery.error.message} />;
    }
    return (
      <DetailErrorState
        notFound={isNotFound}
        message={detailQuery.error.message}
        language={language}
      />
    );
  }

  const detail = detailQuery.data;
  const fileUrl = invoiceFileUrl(detail.id);
  const activePdfHighlight = pdfHighlight?.invoiceId === detail.id ? pdfHighlight.target : null;
  const editingDisabled =
    detail.status === 'queued' || detail.status === 'processing' || detail.status === 'posted';

  async function handleSubmit(blob: InvoiceBlob) {
    setSaveError(null);
    try {
      await updateMutation.mutateAsync({ blob });
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : language === 'en'
            ? 'Saving failed'
            : 'Speichern fehlgeschlagen';
      setSaveError(msg);
      throw err;
    }
  }

  return (
    <section className="flex flex-col gap-4">
      <InvoiceDetailHeader
        detail={detail}
        statusSlot={
          <>
            <InvoiceManualReviewActions invoiceId={detail.id} status={detail.status} />
            <ReprocessButton invoiceId={detail.id} status={detail.status} />
          </>
        }
        rightSlot={
          <div className="flex flex-wrap items-center gap-2">
            <DeleteInvoiceButton
              invoiceId={detail.id}
              status={detail.status}
              onDeleted={() => {
                void navigate({ to: '/invoices' });
              }}
            />
            <InvoiceExportMenu
              invoiceId={detail.id}
              hasBlob={detail.blob !== null}
              status={detail.status}
            />
          </div>
        }
      />

      <InvoiceErrorBanner detail={detail} />

      {saveError ? (
        <div
          className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive"
          role="alert"
        >
          {saveError}
        </div>
      ) : null}

      <InvoiceExceptionFocusPanel detail={detail} />
      <InvoiceSapSyncPanel detail={detail} />

      <InvoiceDetailLayout
        pdf={
          <PdfPreview
            src={fileUrl}
            filename={detail.filename}
            mimeType={detail.mimeType}
            highlightTarget={activePdfHighlight}
          />
        }
        fields={
          <InvoiceEditForm
            blob={detail.blob ?? makeEmptyBlob()}
            tenantId={detail.tenantId}
            tenantName={detail.tenantName}
            disabled={editingDisabled}
            materialIssues={detail.sapSync?.materialIssues ?? []}
            onSubmit={handleSubmit}
            onFieldFocus={(field) => {
              setPdfHighlight({
                invoiceId: detail.id,
                target: {
                  fieldPath: field.name,
                  label: field.label,
                  value: field.value,
                },
              });
            }}
          />
        }
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <InvoiceCommentsPanel invoiceId={detail.id} />
        <InvoiceAuditTrail invoiceId={detail.id} />
      </div>
    </section>
  );
}

function DetailSkeleton() {
  // Don't import this hook eagerly to keep the loading frame cheap.
  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <div className="h-3 w-32 animate-pulse rounded bg-muted" />
        <div className="h-7 w-64 animate-pulse rounded bg-muted" />
        <div className="h-3 w-40 animate-pulse rounded bg-muted" />
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card className="h-[60vh] animate-pulse" />
        <Card className="h-[60vh] animate-pulse" />
      </div>
    </section>
  );
}

interface DetailErrorStateProps {
  notFound: boolean;
  message: string;
  language: 'de' | 'en';
}

function DetailErrorState({ notFound, message, language }: DetailErrorStateProps) {
  return (
    <Card className="mx-auto max-w-xl">
      <CardContent className="flex flex-col items-center gap-4 py-16 text-center">
        <h2 className="text-lg font-semibold">
          {notFound
            ? language === 'en'
              ? 'Invoice not found'
              : 'Rechnung nicht gefunden'
            : language === 'en'
              ? 'Invoice could not be loaded'
              : 'Rechnung konnte nicht geladen werden'}
        </h2>
        <p className="text-sm text-muted-foreground">{message}</p>
        <Link to="/invoices">
          <Button variant="outline">
            {language === 'en' ? 'Back to invoice list' : 'Zur Rechnungsliste'}
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
}
